import React, { useCallback, useMemo } from "react";
import { ContextMenuItem, Icon, Text } from "@salestrak/ui";

import { UNSETTABLE_FIELD_TYPES } from "#app/components/automations/field-capabilities";
import { useAutomateFromView } from "#app/hooks/automations/use-automate-from-view";
import { useAutomationsDialogActions } from "#app/lib/automations-dialog-context";
import { useCustomFieldsDialogActions } from "#app/lib/custom-fields-dialog-context";
import { usePermission } from "#app/lib/hooks/use-permission";
import type { CustomFieldRow } from "#app/lib/data";

// Every table keys its custom-field columns `cf:<customFieldId>` (the issues,
// accounts and contacts tables share the convention), which is how this menu
// recognizes them among the built-in columns.
const CF_COLUMN_PREFIX = "cf:";

/**
 * The custom-field section of a table column header's context menu, for
 * `DataTable`'s `renderColumnMenuItems`: "Edit field" (opens the Custom Field
 * Manager with that field's edit panel up, `custom_fields.manage` only) and
 * "Automate field" (opens the automations builder with an Update-field action
 * pre-selected to the column's field — workflow views only, and never for
 * field types automations cannot set). Built-in columns and columns with
 * nothing to offer return null, so the library skips the section entirely.
 */
export function useCustomFieldColumnMenuItems({
  fields,
  workflowId = null,
}: {
  /** The workspace field pool the table's `cf:` columns render. */
  fields: readonly CustomFieldRow[];
  /** Single-workflow scope for the Automate entry; absent on cross-workflow
   *  and non-workflow surfaces, which drops that entry. */
  workflowId?: string | null | undefined;
}): (columnId: string) => React.ReactNode {
  const canManageFields = usePermission("custom_fields.manage");
  const { canAutomate } = useAutomateFromView(workflowId);
  const { openDialog: openFieldsDialog } = useCustomFieldsDialogActions();
  const { openDialog: openAutomationsDialog } = useAutomationsDialogActions();

  const fieldByColumnId = useMemo(
    () =>
      new Map(
        fields.map((field) => [
          `${CF_COLUMN_PREFIX}${field.customFieldId}`,
          field,
        ]),
      ),
    [fields],
  );

  return useCallback(
    (columnId: string) => {
      const field = fieldByColumnId.get(columnId);
      if (!field) return null;
      const showAutomate =
        canAutomate &&
        workflowId != null &&
        !UNSETTABLE_FIELD_TYPES.includes(field.type);
      if (!canManageFields && !showAutomate) return null;
      return (
        <>
          {canManageFields ? (
            <ContextMenuItem
              onClick={() => openFieldsDialog({ editField: field })}
            >
              <Icon name="pencil" size="lg" color="muted" />
              <Text size="sm">Edit field</Text>
            </ContextMenuItem>
          ) : null}
          {showAutomate ? (
            <ContextMenuItem
              onClick={() =>
                openAutomationsDialog({
                  type: "automate-field",
                  workflowId,
                  customFieldId: field.customFieldId,
                })
              }
            >
              <Icon name="zap" size="lg" color="muted" />
              <Text size="sm">Automate field</Text>
            </ContextMenuItem>
          ) : null}
        </>
      );
    },
    [
      fieldByColumnId,
      canManageFields,
      canAutomate,
      workflowId,
      openFieldsDialog,
      openAutomationsDialog,
    ],
  );
}
